import type { AdoptionRangeQuery, SerializedMeter } from '@enmon/commons/lib/enmon';
import type {
  InventoryMeter,
  SerializedInventoryLocation,
  SerializedInventoryMeterReadingGroup,
  SerializedManufacturer,
  SerializedMeterAddon,
  SerializedMeterModel,
} from '@enmon/commons/lib/tools';
import type {
  AuditLogsFilter,
  CreditInvoiceFilter,
  FloorplanFilter,
  FloorplanMetersFilter,
  GetCustomersArg,
  GetGatewaysParams,
  GetLicensesAnalysisArg,
  GetLicensesArg,
  GetMeterModelFilesArg,
  GetMeterModuleFilesArg,
  GetMetersArgs,
  HardwaresFilter,
  InventoryLocationBuildingFilter,
  InventoryLocationFloorplansFilter,
  ManufacturersApiFilter,
  MeterModelsApiFilter,
  MeterModulesApiFilter,
  MetersFilter,
  MetersByMeterModelsFilter,
} from 'lib/services';
import type { FilesFilter } from 'lib/services/backend-gateway/filesApi';
import type { IntegrationsFilter, RunArg, RunsFilter } from 'lib/services/backend-gateway/integrationsApi';
import type { ManualRecordsFilter } from 'lib/services/backend-gateway/manualRecordsApi';
import type { RevisionsFilter } from 'lib/services/backend-gateway/revisionsApi';
import type { HardwareItemsFilter } from 'lib/services/hardwareItemsApi';
import type {
  FindMeterCompatibleHardwarePackagesArg,
  FindOneHardwarePackageArg,
  HardwarePackagesFilter,
} from 'lib/services/hardwarePackagesApi';
import type { HardwareVariantsFilter } from 'lib/services/hardwareVariantsApi';
import type { ReadingGroupsFilter } from 'lib/services/inventoryMeterReadingGroupsApi';
import type { MeterEventsFilter } from 'lib/services/meterEventsApi';
import type { PersonsFilter } from 'lib/services/personsApi';
import type { ThirdPartiesFilter } from 'lib/services/thirdPartiesApiClient';
import type { Merge } from 'type-fest';
import type { MeterReadingsFilter } from 'types/backendGateway/analysisApi';
import type { EstablishmentsFilter } from 'types/backendGateway/establishmentsApi';
import type { CustomersAdoptionRequest } from '../lib/services/backend-gateway/adoptionApi';
import type { DecoderFilter, PostDecoderArg } from '../lib/services/backend-gateway/decoderApi';
import type { SupplyPointsFilter } from '../types/backendGateway/supplyPointsApi';
import type { StrictOmit } from '@enmon/commons';

export const QUERY_KEYS = {
  adoption: {
    all: () => ['adoption'] as const,
    customers: (query: CustomersAdoptionRequest) => [...QUERY_KEYS.adoption.all(), 'customers', query] as const,
    customer: (customerId: string, range: AdoptionRangeQuery) =>
      [...QUERY_KEYS.adoption.all(), 'customers', customerId, range] as const,
    user: (customerId: string, userId: string, range: AdoptionRangeQuery) =>
      [...QUERY_KEYS.adoption.all(), 'customers', customerId, 'users', userId, range] as const,
  },
  analysis: {
    all: () => ['analysis'] as const,
    dataIntegrity: (filter: Record<string, unknown>) =>
      [...QUERY_KEYS.analysis.all(), 'data-integrity', filter] as const,
    readings: (filter: MeterReadingsFilter) => [...QUERY_KEYS.analysis.all(), 'readings', filter] as const,
  },
  customers: {
    all: () => ['customers'] as const,
    filter: (filter: GetCustomersArg) => [...QUERY_KEYS.customers.all(), filter] as const,

    licenses: (filter: GetLicensesArg = {}) => [...QUERY_KEYS.customers.all(), 'licenses', filter] as const,
    licensesAnalysis: (filter: GetLicensesAnalysisArg) =>
      [...QUERY_KEYS.customers.all(), 'licenses-analysis', filter] as const,
    integrationConfig: (customerId: string) => [...QUERY_KEYS.customers.one(customerId), 'integration-config'] as const,
    auditLogs: (customerId: string, filter: AuditLogsFilter) =>
      [...QUERY_KEYS.customers.one(customerId), 'audit-logs', filter] as const,
    one: (customerId: string) => [...QUERY_KEYS.customers.all(), customerId] as const,
  },
  decoder: {
    get: (filter: DecoderFilter) => ['meter', 'decoder', 'get', filter] as const,
    post: (data: PostDecoderArg) => ['meter', 'decoder', 'post', data] as const,
  },
  documents: {
    all: () => ['documents'] as const,
  },
  establishments: {
    all: () => ['establishments'] as const,
    filter: (filter: EstablishmentsFilter) => [...QUERY_KEYS.establishments.all(), filter] as const,
  },
  files: {
    all: () => ['files'] as const,
    filter: (filter: FilesFilter = {}) => [...QUERY_KEYS.files.all(), filter] as const,
    one: (fileId: string) => [...QUERY_KEYS.files.all(), fileId] as const,
    link: (fileId?: string) => ['files', fileId, 'link'] as const,
    thumbnail: (fileId?: string) => ['files', fileId, 'thumbnail'] as const,
    rotate: (fileId: string) => ['files', fileId, 'rotate'] as const,
  },
  hardwares: {
    all: () => ['hardwares'] as const,
    filter: (filter: HardwaresFilter | undefined) => [...QUERY_KEYS.hardwares.all(), filter] as const,
  },
  hardwarePackages: {
    all: () => ['hardware-packages'] as const,
    one: (id: FindOneHardwarePackageArg) => [...QUERY_KEYS.hardwarePackages.all(), 'one', id] as const,
    filter: (filter: HardwarePackagesFilter) => [...QUERY_KEYS.hardwarePackages.all(), filter] as const,
    findCompatibleWithMeters: (meters: FindMeterCompatibleHardwarePackagesArg['meters']) =>
      [...QUERY_KEYS.hardwarePackages.all(), 'find-compatible-with-meters', meters] as const,
  },
  hardwareItems: {
    all: () => ['hardware-items'] as const,
    filter: (filter: HardwareItemsFilter) => [...QUERY_KEYS.hardwareItems.all(), filter] as const,
  },
  hardwareVariants: {
    all: () => ['hardware-variants'] as const,
    one: (id: FindOneHardwarePackageArg) => [...QUERY_KEYS.hardwareVariants.all(), 'one', id] as const,
    filter: (filter: HardwareVariantsFilter) => [...QUERY_KEYS.hardwareVariants.all(), filter] as const,
  },
  persons: {
    all: () => ['persons'] as const,
    filter: (filter: PersonsFilter) => [...QUERY_KEYS.persons.all(), filter] as const,
  },
  inventoryLocations: {
    all: () => ['inventory-locations'] as const,
    one: (locationId: SerializedInventoryLocation['id']) =>
      [...QUERY_KEYS.inventoryLocations.all(), locationId] as const,
    filter: (filter: Record<string, unknown> = {}) => [...QUERY_KEYS.inventoryLocations.all(), filter] as const,
    floorplans: (locationId: SerializedInventoryLocation['id'], filter?: InventoryLocationFloorplansFilter) =>
      [...QUERY_KEYS.inventoryLocations.one(locationId), 'floorplans', filter] as const,
    meters: (locationId: SerializedInventoryLocation['id'], filter?: MetersFilter) =>
      [...QUERY_KEYS.inventoryLocations.one(locationId), 'meters', filter] as const,
    metersSummary: (locationId: SerializedInventoryLocation['id']) =>
      [...QUERY_KEYS.inventoryLocations.one(locationId), 'meters-summary'] as const,
  },
  inventoryMeters: {
    all: () => ['inventory-meters'] as const,
    filter: (filter: MetersFilter) => [...QUERY_KEYS.inventoryMeters.all(), filter] as const,
    one: (id: InventoryMeter['id'], filter: MetersFilter) => [...QUERY_KEYS.inventoryMeters.all(), id, filter] as const,
    getHierarchyMeters: (filter: MetersFilter) =>
      [...QUERY_KEYS.inventoryLocations.all(), 'hierarchy', filter] as const,
  },
  inventoryMeterReadingGroups: {
    all: () => ['inventory-meter-reading-groups'] as const,
    one: (id: SerializedInventoryMeterReadingGroup['id']) =>
      [...QUERY_KEYS.inventoryMeterReadingGroups.all(), id] as const,
    filter: (filter: ReadingGroupsFilter) =>
      [...QUERY_KEYS.inventoryMeterReadingGroups.all(), 'filter', filter] as const,
  },
  inventoryLocationBuildings: {
    all: () => ['inventory-location-buildings'] as const,
    filter: (filter: InventoryLocationBuildingFilter) =>
      [...QUERY_KEYS.inventoryLocationBuildings.all(), filter] as const,
  },
  inventoryLocationBuildingFloorplans: {
    all: () => ['inventory-location-buildings-floorplans'] as const,
    meters: (floorplanId: number, filter: FloorplanMetersFilter) =>
      [...QUERY_KEYS.inventoryLocationBuildingFloorplans.all(), floorplanId, 'meters', filter] as const,
    filter: (filter: FloorplanFilter) => [...QUERY_KEYS.inventoryLocationBuildingFloorplans.all(), filter] as const,
  },
  meterModules: {
    all: () => ['meter-modules'] as const,
    one: (moduleId: SerializedMeterAddon['id']) => [...QUERY_KEYS.meterModules.all(), moduleId] as const,
    filter: (filter: MeterModulesApiFilter) => [...QUERY_KEYS.meterModules.all(), filter] as const,
    files: (moduleId: GetMeterModuleFilesArg['moduleId'], arg?: StrictOmit<GetMeterModuleFilesArg, 'moduleId'>) =>
      [...QUERY_KEYS.meterModules.one(moduleId), 'files', arg] as const,
  },
  meterModels: {
    all: () => ['meter-models'] as const,
    one: (modelId: SerializedMeterModel['id']) => [...QUERY_KEYS.meterModels.all(), modelId] as const,
    filter: (filter: MeterModelsApiFilter) => [...QUERY_KEYS.meterModels.all(), filter] as const,
    files: (modelId: GetMeterModelFilesArg['modelId'], arg?: StrictOmit<GetMeterModelFilesArg, 'modelId'>) =>
      [...QUERY_KEYS.meterModels.one(modelId), 'files', arg] as const,
    meters: (modelId: SerializedMeterModel['id'], filter?: MetersByMeterModelsFilter) =>
      [...QUERY_KEYS.meterModels.one(modelId), 'meters', filter] as const,
  },
  manufacturers: {
    all: () => ['manufacturers'] as const,
    filter: (filter: ManufacturersApiFilter) => [...QUERY_KEYS.manufacturers.all(), filter] as const,
    one: (manufacturerId: SerializedManufacturer['id']) => [...QUERY_KEYS.manufacturers.all(), manufacturerId] as const,
  },
  meterEvents: {
    all: () => ['meter-events'] as const,
    find: (filter: MeterEventsFilter) => [...QUERY_KEYS.meterEvents.all(), filter, 'find'] as const,
  },
  meters: {
    all: () => ['meters'] as const,
    filter: (
      filter?: Merge<GetMetersArgs['filter'], { sorts?: string[] }>,
      populate?: GetMetersArgs['populate'],
      view?: GetMetersArgs['view'],
    ) => [...QUERY_KEYS.meters.all(), filter, populate, view] as const,
    one: (meterId: SerializedMeter['_id']) => [...QUERY_KEYS.meters.all(), meterId] as const,
    getChildren: (meterId: SerializedMeter['_id']) => [...QUERY_KEYS.meters.one(meterId), 'children'] as const,
    registers: {
      all: (meterId: SerializedMeter['_id']) => [...QUERY_KEYS.meters.all(), meterId, 'registers'] as const,
    },
  },
  creditInvoices: {
    all: () => ['resellers', 'credit-invoices'] as const,
    filter: (filter: CreditInvoiceFilter) => [...QUERY_KEYS.creditInvoices.all(), filter] as const,
  },
  gateways: {
    all: () => ['gateways'] as const,
    filter: (filter: GetGatewaysParams) => [...QUERY_KEYS.gateways.all(), filter] as const,
  },
  integrations: {
    all: () => ['integrations'] as const,
    connectors: () => [...QUERY_KEYS.integrations.all(), 'connectors'] as const,
    health: (limit?: number) => [...QUERY_KEYS.integrations.all(), 'health', limit] as const,
    filter: (filter: IntegrationsFilter) => [...QUERY_KEYS.integrations.all(), filter] as const,
    one: (integrationId: string) => [...QUERY_KEYS.integrations.all(), integrationId] as const,
    runs: (filter: RunsFilter) => [...QUERY_KEYS.integrations.one(filter.integrationId), 'runs', filter] as const,
    run: (arg: RunArg) => [...QUERY_KEYS.integrations.one(arg.integrationId), 'runs', arg.runId] as const,
  },
  thirdParties: {
    all: () => ['third-parties'] as const,
    filter: (filter: ThirdPartiesFilter) => [...QUERY_KEYS.thirdParties.all(), filter] as const,
  },
  manualRecords: {
    all: () => ['manual-records'] as const,
    filter: (filter: ManualRecordsFilter) => [...QUERY_KEYS.manualRecords.all(), filter] as const,
  },
  revisions: {
    all: () => ['revisions'] as const,
    filter: (filter: RevisionsFilter) => [...QUERY_KEYS.revisions.all(), filter] as const,
  },
  supplyPoints: {
    all: () => ['supply-points'] as const,
    filter: (filter: SupplyPointsFilter) => [...QUERY_KEYS.supplyPoints.all(), filter] as const,
    getMetadata: (filter: SupplyPointsFilter) => [...QUERY_KEYS.supplyPoints.all(), 'metadata', filter] as const,
  },
} as const;

export const MUTATION_KEYS = {
  customers: {
    create: () => ['customers', 'create'] as const,
    updateOne: () => ['customers', 'update-one'] as const,
    clearDashboardCache: () => ['customers', 'clear-dashboard-cache'] as const,
  },
  dataQuality: {
    createReport: () => ['data-quality', 'create-report'] as const,
  },
  documents: {
    parse: () => ['documents', 'parse'] as const,
  },
  integrations: {
    create: () => ['integrations', 'create'] as const,
    updateOne: () => ['integrations', 'update-one'] as const,
    putConfig: () => ['integrations', 'put-config'] as const,
    putCredentials: () => ['integrations', 'put-credentials'] as const,
    deleteOne: () => ['integrations', 'delete-one'] as const,
    runNow: () => ['integrations', 'run-now'] as const,
  },
  hardwares: {
    create: () => ['hardwares', 'create'] as const,
    updateOne: () => ['hardwares', 'update-one'] as const,
    deleteOne: () => ['hardwares', 'delete-one'] as const,
    uploadHardwareList: () => ['hardwares', 'upload-hardware-list'] as const,
  },
  hardwarePackages: {
    create: () => ['hardware-packages', 'create'] as const,
    updateOne: () => ['hardware-packages', 'update-one'] as const,
    deleteOne: () => ['hardware-packages', 'delete-one'] as const,
  },
  hardwareItems: {
    create: () => ['hardware-items', 'create'] as const,
    updateOne: () => ['hardware-items', 'update-one'] as const,
    deleteOne: () => ['hardware-items', 'delete-one'] as const,
  },
  hardwareVariants: {
    create: () => ['hardware-variants', 'create'] as const,
    updateOne: () => ['hardware-variants', 'update-one'] as const,
    deleteOne: () => ['hardware-variants', 'delete-one'] as const,
    assignHardwarePackage: () => ['hardware-packages', 'assign-hardware-package'] as const,
  },
  persons: {
    create: () => ['persons', 'create'] as const,
    updateOne: () => ['persons', 'update-one'] as const,
    deleteOne: () => ['persons', 'delete-one'] as const,
  },
  inventoryLocationBuildingFloorplans: {
    create: () => ['inventory-location-building-floorplans', 'create'] as const,
    deleteOne: () => ['inventory-location-building-floorplans', 'delete-one'] as const,
  },
  templates: {
    uploadTemplate: () => ['templates', 'upload-template'] as const,
  },
  resellers: {
    updateOne: () => ['resellers', 'update-one'] as const,
  },
  meters: {
    createOne: () => ['meter', 'create-one'] as const,
    updateOne: () => ['meter', 'update-one'] as const,
    replay: () => ['meter', 'replay'] as const,
    mbusToJson: () => ['meter', 'mbus-to-json'] as const,
    deleteOne: () => ['meter', 'delete-one'] as const,
    bulkDelete: () => ['meter', 'bulk-delete'] as const,
    activate: () => ['meter', 'activate'] as const,
    deactivate: () => ['meter', 'deactivate'] as const,
    activationJob: () => ['meter', 'activation-job'] as const,
    deactivationJob: () => ['meter', 'deactivation-job'] as const,
    bulkActivate: () => ['meter', 'bulk-activate'] as const,
    bulkDeactivate: () => ['meter', 'bulk-deactivate'] as const,
    move: () => ['meter', 'move'] as const,
    merge: () => ['meter', 'merge'] as const,
    readingsManagement: {
      adjustReadings: () => ['meter', 'adjust-counter'] as const,
      changeMeterSerial: () => ['meter', 'change-serial-counter'] as const,
      deleteReadings: () => ['meter', 'delete-counter'] as const,
    },
    setActiveHardwareVariant: () => ['meter', 'set-active-hardware-variant'] as const,
  },
  inventories: {
    create: () => ['inventories', 'create'] as const,
    updateOne: () => ['inventories', 'update-one'] as const,
  },
  inventoryLocations: {
    create: () => ['inventory-locations', 'create'] as const,
    updateOne: () => ['inventory-locations', 'update-one'] as const,
    generateApiKey: () => ['inventory-locations', 'generate-api-key'] as const,
    sync: () => ['inventory-locations', 'sync'] as const,
  },
  creditInvoices: {
    create: () => ['credit-invoices', 'create'] as const,
    updateOne: () => ['credit-invoices', 'update-one'] as const,
    deleteOne: () => ['credit-invoices', 'delete-one'] as const,
  },
  inventoryMeters: {
    updateOne: () => ['inventory-meters', 'update-one'] as const,
    assignTechnicalSolution: () => ['inventory-meters', 'assign-technical-solution'] as const,
  },
  inventoryMeterHardwares: {
    updateMany: () => ['inventory-meter-hardwares'] as const,
  },
  inventoryMeterReadingPhotos: {
    deleteOne: () => ['inventory-meter-reading-photos', 'delete-one'] as const,
  },
  inventoryMeterReadingGroups: {
    create: () => ['inventory-meter-reading-groups', 'create'] as const,
    deleteOne: () => ['inventory-meter-reading-groups', 'delete-one'] as const,
    updateOne: () => ['inventory-meter-reading-groups', 'update-one'] as const,
  },
  files: {
    create: () => ['files', 'create'] as const,
    update: () => ['files', 'update'] as const,
    delete: () => ['files', 'delete'] as const,
    rotate: () => ['files', 'rotate'] as const,
    resetRotation: () => ['files', 'resetRotation'] as const,
  },
  manualRecords: {
    create: () => ['manual-records', 'create'] as const,
    updateOne: () => ['manual-records', 'update-one'] as const,
    deleteOne: () => ['manual-records', 'delete-one'] as const,
  },
  revisions: {
    updateOne: () => ['revisions', 'update-one'] as const,
    deleteOne: () => ['revisions', 'delete-one'] as const,
    revert: () => ['revisions', 'revert'] as const,
  },
  manufacturers: {
    create: () => ['manufacturers', 'create'] as const,
    updateOne: () => ['manufacturers', 'update-one'] as const,
    deleteOne: () => ['manufacturers', 'delete-one'] as const,
  },
  meterModels: {
    create: () => ['meter-models', 'create'] as const,
    updateOne: () => ['meter-models', 'update-one'] as const,
    assignToModule: () => ['meter-models', 'assign-to-module'] as const,
    deleteOne: () => ['meter-models', 'delete-one'] as const,
  },
  meterModules: {
    create: () => ['meter-modules', 'create'] as const,
    updateOne: () => ['meter-modules', 'update-one'] as const,
    assignToModel: () => ['meter-modules', 'assign-to-model'] as const,
    deleteOne: () => ['meter-modules', 'delete-one'] as const,
  },
};
